import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { ColorTilesValuesFragmentFragmentDoc } from '@packages/gql/generated/shopping/ColorTilesValuesFragmentFragmentDoc';
import { ColorButton } from '@packages/shared/src/components/ColorButton/ColorButton';
import { Stack } from '@packages/shared/src/components/Stack/Stack';
import { Typography } from '@packages/shared/src/components/Typography/Typography';
import { useRouter } from 'next/navigation';
import { templateNames } from '../../hooks/useResponsiveAkamaiTemplate/config';
import type { SetSelectedVariation } from '../../types/types';
import { useVariationFormattedLink } from '../utils/useVariationFormattedLink';
import { activeStyles, defaultStyles } from './styles';

const IMAGETEMPLATE = templateNames.variationSelect;

/* GraphQL */ `
  fragment ColorTilesValuesFragment on ProductDimension {
    values {
      displayText
      image
      isSelected
      rgb
      sku
    }
  }
`;

export type ColorTilesDimensionProps = {
  /**
   * The GraphQL dimension data for the given product
   */
  dimensionData: FragmentType<typeof ColorTilesValuesFragmentFragmentDoc>;
  onChange?: SetSelectedVariation;
};

/**
 * ColorTilesDimension to choose a product color on Detailview
 * */
export const ColorTilesDimension = ({ dimensionData, onChange }: ColorTilesDimensionProps) => {
  const dimension = getFragmentData(ColorTilesValuesFragmentFragmentDoc, dimensionData);
  const selectedColor = dimension.values.find((value) => value.isSelected);
  const { push } = useRouter();
  const createVariationLink = useVariationFormattedLink();

  return (
    <>
      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
        {selectedColor ? selectedColor.displayText : ''}
      </Typography>
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={1.5} sx={{ mt: 0.75 }}>
        {dimension.values.map(({ sku, image, rgb, displayText, isSelected }) => {
          const isActive = isSelected || false;
          return (
            <ColorButton
              onClick={() => {
                // why is the sku nullable?!
                if (sku) {
                  if (onChange) {
                    onChange(sku);
                  } else {
                    push(createVariationLink(sku));
                  }
                }
              }}
              key={`colorBtn${sku}`}
              minimal={false}
              image={image ? `${image}?${IMAGETEMPLATE}` : undefined}
              fitBackgroundImage
              color={rgb ? `#${rgb}` : ''}
              name={displayText || ''}
              active={isActive}
              sx={isActive ? activeStyles : defaultStyles}
            />
          );
        })}
      </Stack>
    </>
  );
};
